
export default function ProcessSection() {
  const steps = [
    {
      number: '1',
      title: '診断（現状の見える化）',
      description: '交換時間・回数・不具合要因を数値化',
      duration: '1-2週間',
      icon: 'ri-search-line',
      color: 'yellow'
    },
    {
      number: '2',
      title: '本診断',
      description: '実際の工具、３Dモデルをお預かりし、開発元（ドイツ）にて検証',
      duration: '3ヶ月',
      icon: 'ri-test-tube-line',
      color: 'gray'
    },
    {
      number: '3',
      title: '本導入',
      description: 'venturion/TMSと連携し、測定→交換→証拠を一体化',
      duration: '1-3ヶ月',
      icon: 'ri-rocket-line',
      color: 'yellow'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            【導入プロセス｜"速く小さく始める"】
          </h2>
          <p className="text-xl text-gray-600">
            リスクを最小化しながら確実な効果を実現
          </p>
        </div>

        <div className="relative">
          {/* プロセスライン */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gray-200">
            <div className="h-full bg-gradient-to-r from-yellow-500 via-gray-500 to-yellow-500"></div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 bg-${step.color}-100 rounded-full flex items-center justify-center mx-auto mb-4 relative z-10`}>
                      <i className={`${step.icon} text-2xl text-${step.color}-600`}></i>
                    </div>
                    <div className={`w-8 h-8 bg-${step.color}-600 text-white rounded-full flex items-center justify-center mx-auto text-lg font-bold absolute top-0 right-0 transform translate-x-2 -translate-y-2`}>
                      {step.number}
                    </div>
                  </div>

                  <div className="text-center space-y-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                    <div className={`inline-block bg-${step.color}-50 text-${step.color}-700 px-4 py-2 rounded-full text-sm font-semibold border border-${step.color}-200`}>
                      期間：{step.duration}
                      {step.number === '3' && (
                        <span className="block mt-1 text-xs">
                          （ただし製作納期が約15か月要します）
                        </span>
                      )}
                    </div>
                    {step.number === '3' && (
                      <div className="mt-3 text-xs text-gray-500 leading-relaxed">
                        ※欧州／米州地域にて大変多くの受注を頂いており、製作納期が延長される可能性がございます。
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">
              なぜ段階的導入なのか？
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-yellow-600 text-sm"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">リスク最小化</h4>
                  <p className="text-gray-600 text-sm">小規模から始めて効果を確認してから本格導入</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-yellow-600 text-sm"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">現場適応</h4>
                  <p className="text-gray-600 text-sm">御社の生産環境に最適化したカスタマイズ</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-yellow-600 text-sm"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">効果実証</h4>
                  <p className="text-gray-600 text-sm">数値で効果を確認してから投資判断</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/6bf07f0bdee7fa35f6ec2aadaddf37c9.jpg"
              alt="導入プロセス"
              className="rounded-lg shadow-lg max-w-md w-full object-cover"
            />
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-8 rounded-2xl">
            <h3 className="text-2xl font-bold mb-4">まずは現状診断から始めませんか？</h3>
            <p className="text-lg mb-6">
              御社の段取り作業を数値化し、改善ポテンシャルを明確にします
            </p>
            <button className="bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-yellow-600 transition-colors cursor-pointer whitespace-nowrap">
              無料診断を申し込む
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
